/* eslint-disable react-hooks/exhaustive-deps */
import { BoundingBox, Time } from '@eagle/common';
import { Thing, ThingLastLocation, ThingType } from '@eagle/core-data-types';
import type { ClusterPoint } from '@ethings/ui-toolkit/map';
import { ClusterLayer } from '@ethings/ui-toolkit/map';
import { captureException } from '@sentry/react';
import L from 'leaflet';
import { DateTime } from 'luxon';
import { FC, useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { useMap } from 'react-leaflet';
import { useParams } from 'react-router';
import { useAuthenticated } from '../../auth/auth';
import { evaluate } from '../../evaluator';
import { LastThingEvents, LastThingEventsByFeature, useGeoLocation, useLastThingState, useMapContext, useMapZoomLimits, useUiTemplate } from '../../hooks';
import { createThingFaultStatusLookup, useThingsFaultStatus } from '../../hooks/use-things-fault-status';
import { useSearch } from '../../pages/list/use-search';
import { CacheDataTypes, MapPosition } from '../../types';
import { Nullable } from '../../types/common';
import { ThingFaultStatus } from '../../types/thing-fault-status';
import { DEFAULT_MAP_MARKER } from '../../ui-templates';
import { areBoundsInside, areObjectsEqual, getThingLocations, toBBox } from '../../util/maps';
import { FetchOne, FetchOneOfAll } from '../fetch';
import { useMapLayers } from './hooks';
import './leaflet-styles.min.css';

const FIT_BOUNDS_PADDING = 125;

interface Props {
  disableMarkerClick?: boolean;
  enableSettingPosition?: boolean;
  savedPosition?: Nullable<MapPosition>;
  loading?: boolean;
  setLoading?: (loading: boolean) => void;
  setLocationLoading?: (loading: boolean) => void;
  setSavedPosition?: (position: MapPosition) => void;
  handleNoLocations?: () => void;
  handleLoadingError?: () => void;
}

interface MarkerProps {
  disableMarkerClick?: boolean;
  fetchState: (thingId: string) => Promise<LastThingEvents | undefined>;
  faultsByThingId?: Record<string, ThingFaultStatus>;
  isSelected?: boolean;
  position: L.LatLng;
  thingId: string;
  template: object;
}

interface RenderFactoryProps {
  disableMarkerClick?: boolean;
  faults?: ThingFaultStatus;
  isSelected?: boolean;
  lastThingEvents?: LastThingEventsByFeature;
  position: L.LatLng;
  template: object;
  thing: Thing;
  thingId: string;
  thingType: ThingType;
}

const MAP_REFRESH_INTERVAL_SECONDS = Time.seconds(Number(import.meta.env.VITE_MAP_RELOAD)) || 300;

const toMarkerFaultsContext = (faults?: ThingFaultStatus): Record<string, unknown> | undefined => {
  if (!faults) return undefined;

  return {
    totals: {
      activeCount: faults.activeFaultCount,
      totalFaults: faults.totalFaultCount,
    },
    warning: faults.warning,
    status: faults.status,
    latestFaultOccurredAt: faults.latestFaultOccurredAt,
    activeFaults: faults.activeFaults,
  };
};

const RenderFactory: FC<RenderFactoryProps> = ({ disableMarkerClick, faults, isSelected, lastThingEvents, position, template, thing, thingId, thingType }) => {
  const map = useMap();

  useEffect(() => {
    if (!isSelected) return;
    map.fire('markerLoaded');
  }, [isSelected, map, position.lat, position.lng]);

  const markerFaults = toMarkerFaultsContext(faults);
  const mapMarker = evaluate(template, { disableMarkerClick, position, entityId: thingId, 'data-testid': `map-marker-${thingId}`, dataType: CacheDataTypes.THING, lastEvents: lastThingEvents, thingType, thing, faults: markerFaults, faultStatus: faults });

  return (<>{mapMarker ?? null}</>);
};

const ThingMarker: FC<MarkerProps> = ({ disableMarkerClick, faultsByThingId, fetchState, isSelected, position, thingId, template }) => {
  const [lastThingEvents, setLastThingEvents] = useState<LastThingEventsByFeature>();
  const { lastEventsByThingId } = useMapContext();
  const publishedEvents = lastEventsByThingId[thingId];

  useEffect(() => {
    if (!thingId) return;

    setLastThingEvents(undefined);

    fetchState(thingId).then((events) => {
      setLastThingEvents(events?.byFeature);
    }).catch(captureException);
  }, [fetchState, thingId]);

  useEffect(() => {
    if (publishedEvents) setLastThingEvents(publishedEvents);
  }, [publishedEvents]);

  return <FetchOne
    dataType={CacheDataTypes.THING}
    id={thingId}
    loadingFactory={() => <></>}
    notFoundFactory={() => <></>}
    renderFactory={(thing: Thing) => (
      <FetchOneOfAll
        dataType={CacheDataTypes.THING_TYPE}
        id={thing.thingTypeId}
        loadingFactory={() => <></>}
        notFoundFactory={() => <></>}
        renderFactory={(thingType: ThingType) => (
          <RenderFactory
            disableMarkerClick={disableMarkerClick}
            faults={faultsByThingId?.[thingId]}
            isSelected={isSelected}
            lastThingEvents={lastThingEvents}
            position={position}
            template={template}
            thing={thing}
            thingId={thingId}
            thingType={thingType}
          />
        )}
      />
    )}
  />;
};

export const ThingEntities = ({
  disableMarkerClick,
  enableSettingPosition = true,
  savedPosition,
  setLoading,
  setLocationLoading,
  handleNoLocations,
  handleLoadingError,
}: Props): JSX.Element | null => {
  const map = useMap();
  const { isThingsLayerSelected } = useMapLayers();
  const { maxZoom } = useMapZoomLimits();
  const { latitude: geoLat, longitude: geoLong, loading: geoLoading } = useGeoLocation();
  const { axios } = useAuthenticated();
  const { filters } = useSearch();
  const { thingId } = useParams();
  const debounceTimeout = useRef<Nullable<number>>(null);
  const [callApi, setCallApi] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(DateTime.now());
  const [maxUpdated, setMaxUpdated] = useState<Date | undefined>(undefined);
  const [locationsMap, setLocationsMap] = useState<Nullable<Map<string, ThingLastLocation>>>(null);
  const [bounds, setBounds] = useState<BoundingBox | null>(() => getInitialBounds(map));
  const [currentBounds, setCurrentBounds] = useState<BoundingBox | null>(() => getInitialBounds(map));
  const isInitialPositionSet = useRef(false);
  const [fetchState] = useLastThingState();
  const { setSelectedThingLocation } = useMapContext();

  const [points, setPoints] = useState<ClusterPoint[]>([]);
  const [zoom, setZoom] = useState(map.getZoom());
  const { template, loaded } = useUiTemplate('map-marker', DEFAULT_MAP_MARKER);
  const pointThingIds = useMemo(() => points.map((point) => point.id), [points]);
  const { data: thingsFaultStatusData } = useThingsFaultStatus(
    {
      thingIds: pointThingIds,
      range: 30,
      batchSize: 100,
    },
    {
      enabled: isThingsLayerSelected && loaded && pointThingIds.length > 0,
    },
  );
  const faultsByThingId = useMemo(
    () => createThingFaultStatusLookup(thingsFaultStatusData?.items),
    [thingsFaultStatusData?.items],
  );

  const DEFAULT_MAP_CENTER = L.latLng(0, 0);

  const getBoundsOfArray = (tmpBounds: L.LatLng[]): L.LatLngBounds => {
    const latArray = tmpBounds.map(({ lat }) => lat);
    const lngArray = tmpBounds.map(({ lng }) => lng <= -170 ? lng + 360 : lng);

    const bottomLeft = new L.LatLng(Math.min.apply(null, latArray), Math.min.apply(null, lngArray));
    const topRight = new L.LatLng(Math.max.apply(null, latArray), Math.max.apply(null, lngArray));
    return new L.LatLngBounds(bottomLeft, topRight);
  };

  useEffect(() => {
    if (enableSettingPosition && !thingId && !isInitialPositionSet.current) {
      if (savedPosition) { // 1. saved location
        const { lat, lng, alt } = savedPosition;
        map.setView(new L.LatLng(lat, lng), alt, { animate: false });
        isInitialPositionSet.current = true;
      }
      else if (locationsMap) {
        const initialZoom = map.getZoom();
        const hasLocations = locationsMap.size > 0;
        if (hasLocations) { // 2. location based on data
          const newBounds = getBoundsOfArray(Array.from(locationsMap.values()).map((location) => new L.LatLng(location.latitude, location.longitude)));
          map.fitBounds(newBounds, { animate: false, padding: new L.Point(FIT_BOUNDS_PADDING, FIT_BOUNDS_PADDING) });
        } else if (geoLat && geoLong && !geoLoading) { // 3. location based on current position
          map.setView(new L.LatLng(geoLat, geoLong), undefined, { animate: false });
        } else { // 4. default location
          map.setView(DEFAULT_MAP_CENTER, undefined, { animate: false });
        }
        if (map.getZoom() === initialZoom) {
          // Fire a zoomend event even if the zoom hasn't changed to trigger saving the position to localStorage.
          map.fire('zoomend');
        }
        if (handleNoLocations && !hasLocations) {
          handleNoLocations();
        }
        isInitialPositionSet.current = true;
      }
    }
  }, [map, locationsMap, enableSettingPosition, thingId, handleNoLocations]);

  useEffect(() => {
    const checkAndUpdateData = (): void => {
      if (DateTime.now().diff(lastUpdate, 'seconds').seconds > MAP_REFRESH_INTERVAL_SECONDS) {
        setCallApi(true);
        map.fire('dataLoaded');
      }
    };

    // Interval to check every X minutes
    const intervalId = setInterval(checkAndUpdateData, MAP_REFRESH_INTERVAL_SECONDS);

    // Event listener for tab visibility
    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') {
        checkAndUpdateData();
      }
    };

    // Add event listener for visibility change
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      clearInterval(intervalId);  // Clear the interval
      document.removeEventListener('visibilitychange', handleVisibilityChange);  // Remove the event listener
    };
  }, [lastUpdate]);

  useEffect(() => {
    const bounds = map.getBounds();
    if (bounds.isValid()) {
      setBounds(boundsToBoundingBox(bounds));
    }
  }, [thingId, filters]);

  useEffect(() => {
    const handleBoundsChange = (): void => {
      const tmpCurrentBounds = map.getBounds();
      const boundsToUpdate = boundsToBoundingBox(tmpCurrentBounds);

      // Always update the current bounds
      setCurrentBounds(boundsToUpdate);

      if (bounds && tmpCurrentBounds.isValid() && !areObjectsEqual(boundsToUpdate, bounds)) {
        if (!areBoundsInside(boundsToUpdate, bounds)) {
          setBounds(boundsToUpdate);
        }
      }
    };

    const handleZoomChange = (): void => {
      const currentZoom = map.getZoom();
      setZoom(currentZoom);
    };

    map.on('dragend', handleBoundsChange);
    map.on('moveend', handleBoundsChange);
    map.on('zoomend', handleBoundsChange);
    map.on('zoomend', handleZoomChange);

    return () => {
      map.off('dragend', handleBoundsChange);
      map.off('moveend', handleBoundsChange);
      map.off('zoomend', handleBoundsChange);
      map.off('zoomend', handleZoomChange);
    };
  }, [map, bounds]);

  const handleLoading = (refreshCall: boolean): void => {
    if (debounceTimeout.current !== null) {
      clearTimeout(debounceTimeout.current);
    }

    const newTimeout = window.setTimeout(() => {
      getThingLocations(map.getBounds(), axios, locationsMap, handleLoadingError, setLocationsMap, setLastUpdate, setMaxUpdated, setLoading, setLocationLoading, maxUpdated, filters, refreshCall);
    }, 300);

    debounceTimeout.current = newTimeout;
  };

  useEffect(() => {
    handleLoading(false);
    return () => {
      if (debounceTimeout.current !== null) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [bounds, callApi]);

  useEffect(() => {
    if (callApi) {
      handleLoading(true);
      setCallApi(false);
    }
  }, [callApi]);

  useEffect(() => {
    if (!locationsMap) return;
    setPoints(
      Array.from(locationsMap.values()).map((location) => ({
        id: location.thingId,
        coordinates: [location.longitude, location.latitude],
        properties: { updated: DateTime.now().toISO() },
      })),
    );
    if (!thingId) return;
    const selectedThingLocation = Array.from(locationsMap.values()).find((x) => x.thingId === thingId);
    if (selectedThingLocation) setSelectedThingLocation(selectedThingLocation);
  }, [locationsMap, thingId]);

  if (!isThingsLayerSelected || !loaded) return null;

  return (
    <ClusterLayer
      points={points}
      bounds={currentBounds ? toBBox(currentBounds) as [number, number, number, number] : undefined}
      zoom={zoom}
      maxZoom={maxZoom}
      selectedPointId={thingId}
      enableSpider
      renderMarker={(point, position) => (
        <ThingMarker
          disableMarkerClick={disableMarkerClick}
          faultsByThingId={faultsByThingId}
          fetchState={fetchState}
          isSelected={point.id === thingId}
          position={new L.LatLng(position[0], position[1])}
          thingId={point.id}
          template={template}
        />
      )}
      renderSpiderMarker={(point, position) => (
        <ThingMarker
          disableMarkerClick={disableMarkerClick}
          faultsByThingId={faultsByThingId}
          fetchState={fetchState}
          isSelected={point.id === thingId}
          position={new L.LatLng(position[0], position[1])}
          thingId={point.id}
          template={template}
        />
      )}
    />
  );
};

const getInitialBounds = (map: L.Map): Nullable<BoundingBox> => {
  const bounds = map.getBounds();
  if (!bounds.isValid()) return null;
  return boundsToBoundingBox(bounds);
};

const boundsToBoundingBox = (bounds: L.LatLngBounds): BoundingBox => {
  return {
    east: bounds.getEast(),
    north: bounds.getNorth(),
    south: bounds.getSouth(),
    west: bounds.getWest(),
  };
};
